import type { HTMLAttributes, ReactNode } from "react";
import {
  AD_ENTITY_STATUS_LABELS,
  ALERT_SEVERITY_LABELS,
  CONFIRMATION_STATE_LABELS,
  CONVERSATION_STATUS_LABELS,
  LATE_LABEL,
  LEAD_STATUS_LABELS,
  ORDER_STATUS_LABELS,
  RECOMMENDATION_STATUS_LABELS,
  RETURN_STATUS_LABELS,
  SHIPMENT_STATUS_LABELS,
  FOLLOWUP_STATUS_LABELS,
  SETTLEMENT_STATUS_LABELS,
  EXPERIMENT_STATUS_LABELS,
  SITE_STATUS_LABELS,
  DOMAIN_STATUS_LABELS,
  SUBSCRIPTION_STATUS_LABELS,
} from "@digisubs/shared";
import { cn } from "@/lib/cn";

export type Tone = "blue" | "green" | "orange" | "red" | "gray" | "violet" | "pink" | "cyan" | "teal";

const solid: Record<Tone, string> = {
  blue: "bg-primary text-white",
  green: "bg-success-600 text-white",
  orange: "bg-warning-600 text-white",
  red: "bg-danger-600 text-white",
  gray: "bg-[#2a3753] text-ink-2",
  violet: "bg-violet-600 text-white",
  pink: "bg-pink-600 text-white",
  cyan: "bg-[#0e7490] text-white",
  teal: "bg-[#0f766e] text-white",
};

const soft: Record<Tone, string> = {
  blue: "bg-primary/15 text-primary-300 ring-1 ring-inset ring-primary/25",
  green: "bg-success/12 text-[#4ade80] ring-1 ring-inset ring-success/25",
  orange: "bg-warning/12 text-[#fbbf24] ring-1 ring-inset ring-warning/25",
  red: "bg-danger/12 text-[#f87171] ring-1 ring-inset ring-danger/25",
  gray: "bg-[#1b2742] text-ink-2 ring-1 ring-inset ring-line-strong",
  violet: "bg-violet/15 text-[#a78bfa] ring-1 ring-inset ring-violet/25",
  pink: "bg-pink/15 text-[#f472b6] ring-1 ring-inset ring-pink/25",
  cyan: "bg-cyan/12 text-cyan ring-1 ring-inset ring-cyan/25",
  teal: "bg-teal/12 text-[#2dd4bf] ring-1 ring-inset ring-teal/25",
};

// Status pills of the screenshots: dark tinted fill, matching border, light text ("En préparation", "Livré", "Active").
const tinted: Record<Tone, string> = {
  blue: "bg-[#1e3a8a]/70 text-[#bfdbfe] ring-1 ring-inset ring-[#3b82f6]/50",
  green: "bg-[#14532d]/85 text-[#bbf7d0] ring-1 ring-inset ring-[#22c55e]/35",
  orange: "bg-[#78350f]/85 text-[#fde68a] ring-1 ring-inset ring-[#f59e0b]/45",
  red: "bg-[#7f1d1d]/85 text-[#fecaca] ring-1 ring-inset ring-[#ef4444]/45",
  gray: "bg-[#1f2a44] text-[#cbd5e1] ring-1 ring-inset ring-[#334155]",
  violet: "bg-[#4c1d95]/70 text-[#ddd6fe] ring-1 ring-inset ring-[#8b5cf6]/45",
  pink: "bg-[#831843]/70 text-[#fbcfe8] ring-1 ring-inset ring-[#ec4899]/45",
  cyan: "bg-[#164e63]/80 text-[#a5f3fc] ring-1 ring-inset ring-[#22d3ee]/40",
  teal: "bg-[#134e4a]/85 text-[#99f6e4] ring-1 ring-inset ring-[#14b8a6]/40",
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
  variant?: "solid" | "soft" | "tinted";
  size?: "xs" | "sm";
  icon?: ReactNode;
}

export function Badge({ tone = "gray", variant = "soft", size = "sm", icon, className, children, ...rest }: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 whitespace-nowrap font-medium",
        size === "xs" ? "h-5 rounded-md px-1.5 text-[10.5px]" : "h-6 rounded-md px-2 text-[11.5px]",
        variant === "solid" ? solid[tone] : variant === "tinted" ? cn(tinted[tone], "font-semibold") : soft[tone],
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </span>
  );
}

export function Dot({ tone = "blue", className }: { tone?: Tone; className?: string }) {
  const colors: Record<Tone, string> = {
    blue: "bg-primary",
    green: "bg-success",
    orange: "bg-warning",
    red: "bg-danger",
    gray: "bg-muted",
    violet: "bg-violet",
    pink: "bg-pink",
    cyan: "bg-cyan",
    teal: "bg-teal",
  };
  return <span className={cn("inline-block size-2 shrink-0 rounded-full", colors[tone], className)} aria-hidden />;
}

/** Round count badge (unread messages). */
export function CountBadge({ count, tone = "blue", className }: { count: number; tone?: "blue" | "red" | "gray"; className?: string }) {
  if (!count) return null;
  return (
    <span
      className={cn(
        "inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold leading-none",
        tone === "blue" && "bg-primary text-white",
        tone === "red" && "bg-danger text-white",
        tone === "gray" && "bg-panel-3 text-ink-2",
        className,
      )}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

// Status pills: every domain status maps to a tone and its French label. Status is never shown by color alone.

type Domain =
  | "order"
  | "confirmation"
  | "shipment"
  | "conversation"
  | "ad"
  | "recommendation"
  | "alert"
  | "return"
  | "lead"
  | "followup"
  | "settlement"
  | "experiment"
  | "site"
  | "domain"
  | "subscription";

const TONES: Record<Domain, Record<string, Tone>> = {
  order: { new: "cyan", confirmed: "violet", preparing: "orange", shipped: "blue", delivered: "green", returned: "gray", cancelled: "red", late: "red" },
  confirmation: { pending: "gray", requested: "blue", no_answer: "orange", postponed: "orange", confirmed: "green", refused: "red", unreachable: "red" },
  shipment: {
    pending: "orange",
    picked_up: "cyan",
    in_transit: "blue",
    out_for_delivery: "violet",
    delivered: "green",
    failed_attempt: "orange",
    returning: "pink",
    returned: "gray",
    cancelled: "red",
    exception: "red",
    unknown: "gray",
  },
  conversation: { open: "blue", pending: "orange", resolved: "green", snoozed: "gray" },
  ad: { active: "green", paused: "orange", draft: "gray", in_review: "blue", rejected: "red", with_issues: "red", completed: "gray", archived: "gray", deleted: "gray", error: "red" },
  recommendation: { open: "blue", approved: "violet", rejected: "gray", executing: "cyan", executed: "green", failed: "red", expired: "gray", dismissed: "gray" },
  alert: { info: "blue", warning: "orange", critical: "red" },
  return: { expected: "orange", in_transit: "blue", received: "violet", restocked: "green", closed: "gray" },
  lead: { new: "cyan", contacted: "blue", qualified: "violet", converted: "green", lost: "gray" },
  followup: { scheduled: "blue", sent: "green", failed: "red", cancelled: "gray", replied: "violet", skipped: "gray" },
  settlement: { pending: "orange", partial: "blue", settled: "green", disputed: "red" },
  experiment: { draft: "gray", running: "blue", completed: "green", stopped: "orange" },
  site: { draft: "gray", published: "green", archived: "gray" },
  domain: { pending: "orange", verified: "green", failed: "red" },
  subscription: { trialing: "blue", active: "green", past_due: "orange", canceled: "gray", expired: "red" },
};

const LABELS: Record<Domain, Record<string, string>> = {
  order: { ...ORDER_STATUS_LABELS, late: LATE_LABEL },
  confirmation: CONFIRMATION_STATE_LABELS,
  shipment: SHIPMENT_STATUS_LABELS,
  conversation: CONVERSATION_STATUS_LABELS,
  ad: AD_ENTITY_STATUS_LABELS,
  recommendation: RECOMMENDATION_STATUS_LABELS,
  alert: ALERT_SEVERITY_LABELS,
  return: RETURN_STATUS_LABELS,
  lead: LEAD_STATUS_LABELS,
  followup: FOLLOWUP_STATUS_LABELS,
  settlement: SETTLEMENT_STATUS_LABELS,
  experiment: EXPERIMENT_STATUS_LABELS,
  site: SITE_STATUS_LABELS,
  domain: DOMAIN_STATUS_LABELS,
  subscription: SUBSCRIPTION_STATUS_LABELS,
};

export function statusTone(domain: Domain, status: string): Tone {
  return TONES[domain][status] ?? "gray";
}

export function statusLabel(domain: Domain, status: string): string {
  return LABELS[domain][status] ?? status;
}

/** Tinted pill like the screenshots ("En préparation" amber, "Livrée" green, "En transit" blue, "En retard" red). */
export function StatusPill({ domain, status, variant = "tinted", className }: { domain: Domain; status: string; variant?: "solid" | "soft" | "tinted"; className?: string }) {
  return (
    <Badge tone={statusTone(domain, status)} variant={variant} className={className}>
      {statusLabel(domain, status)}
    </Badge>
  );
}

export function NewBadge({ children = "Nouveau" }: { children?: ReactNode }) {
  return (
    <span className="inline-flex h-4 items-center rounded-[4px] bg-[#5b3df5] px-1.5 text-[10px] font-bold leading-none text-white">
      {children}
    </span>
  );
}

export function BetaBadge() {
  return <span className="inline-flex h-5 items-center rounded-md bg-primary px-2 text-[10.5px] font-bold tracking-wide text-white">BETA</span>;
}
